import * as React from "react";
import { HostAdapter } from "./types";

export * from "./types";
export type { ToolResult } from "./shared/toolResult";
export type { EditInfo } from "./shared/editInfo";
export type { Citation } from "./shared/citation";

// The seam's types, and the React plumbing that carries one adapter to everything
// above it. Deliberately imports NO adapter: this module is pulled in by almost every
// component, so an import here would put every host's document layer into every
// bundle. Each entry point resolves its own adapter and hands it to the provider --
// the document hosts through platform/documentHosts, Outlook through platform/outlook.

const HostAdapterContext = React.createContext<HostAdapter | undefined>(undefined);

export const HostAdapterProvider = ({
  adapter,
  children,
}: {
  adapter: HostAdapter;
  children: React.ReactNode;
}) => <HostAdapterContext.Provider value={adapter}>{children}</HostAdapterContext.Provider>;

/**
 * The adapter for the running host.
 *
 * Throws outside the provider rather than resolving one itself. It used to fall back
 * to resolving directly, which meant this module had to import all three document
 * adapters -- and a component rendered without a provider silently got Word's. A
 * loud failure is better than a wrong host, and the entry points always provide one.
 */
export const useHostAdapter = (): HostAdapter => {
  const adapter = React.useContext(HostAdapterContext);
  if (!adapter) {
    throw new Error(
      "useHostAdapter was called outside HostAdapterProvider. Every entry point resolves " +
        "its own adapter and passes it to Providers; a test rendering one of these " +
        "components needs to do the same."
    );
  }
  return adapter;
};
